import { Tag } from '@/design-system/components/tag/Tag';
import { TextBody } from '@/design-system/components/typography';
import { formatDate } from '@/utils/dateFormatting';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { UserEarnActionListResponseDataItem } from '@utx/utx-client';
import { styled, SxProp, View } from 'dripsy';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { getSubscriptionTypeTagColours } from '../../utils/getSubscriptionTypeTagColours';
import { CurrenciesIcon, CurrencyIconVariant } from '../CurrenciesIcon';

const PressableView = styled(Pressable)();

interface ActivityHistoryCardNativeProps {
  subscription: UserEarnActionListResponseDataItem;
  onPress?: () => void;
}

export const ActivityHistoryCardNative: React.FC<ActivityHistoryCardNativeProps> = ({ subscription, onPress }) => {
  const { t } = useTranslation('earn');
  const tagColours = getSubscriptionTypeTagColours(subscription.type);
  const tenor = getEarnTenorTranslationKey(subscription.tenor);
  return (
    <PressableView sx={styles.container} onPress={onPress}>
      <View sx={styles.textDetailsBox}>
        <CurrenciesIcon
          asset={subscription.offerName}
          subText={subscription.category}
          variant={CurrencyIconVariant.activityHistoryNative}
          textSize="textTitle"
          textSemiBold
          subTextSize="textCaption"
          subTextSx={{ color: '$textPrimary' }}
          sx={{ width: '100%', maxWidth: '65%', overflow: 'hidden' }}
        />
        <Tag label={subscription.type} color={tagColours.backgroundColor} textColor={tagColours.color} />
      </View>
      <View sx={styles.textDetailsBox}>
        <TextBody secondary>{t('earn-invest::glossary.date')}</TextBody>
        <TextBody>{formatDate(subscription.subscriptionDate)}</TextBody>
      </View>
      <View sx={styles.textDetailsBox}>
        <TextBody secondary>{t('earn-invest::glossary.ref-id')}</TextBody>
        <TextBody>{subscription.refId}</TextBody>
      </View>
      <View sx={styles.textDetailsBox}>
        <TextBody secondary>{t('earn-invest::glossary.est-apy')}</TextBody>
        <TextBody>{formatAmountByDecimals({ value: subscription.estimatedApyPercent, decimals: 2, suffix: '%' })}</TextBody>
      </View>
      <View sx={styles.textDetailsBox}>
        <TextBody secondary>{t('earn-invest::glossary.tenor')}</TextBody>
        <TextBody>{t(tenor.key, tenor.options)}</TextBody>
      </View>
      <View sx={styles.textDetailsBox}>
        <TextBody secondary>{t('earn-invest::glossary.amount')}</TextBody>
        <TextBody>{formatAmountByDecimals({ value: subscription.amount, decimals: 8, suffix: subscription.asset })}</TextBody>
      </View>
      {subscription.maturityDate && (
        <View sx={styles.textDetailsBox}>
          <TextBody secondary>{t('earn-invest::glossary.maturity')}</TextBody>
          <TextBody>{formatDate(subscription.maturityDate)}</TextBody>
        </View>
      )}
    </PressableView>
  );
};

enum StyleName {
  container = 'container',
  textDetailsBox = 'textDetailsBox'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    padding: '$4',
    mb: '$4',
    borderRadius: 10,
    borderWidth: 0,
    borderColor: '$borderDivider',
    justifyContent: 'space-between',
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 5
    },
    shadowOpacity: 0.08,
    shadowRadius: 15,
    elevation: 10,
    gap: '$3'
  },
  textDetailsBox: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }
};
